import { ReactElement, useMemo } from 'react';

import { AppointmentPractitioner } from '@dreryk/egabinet-appointment-frontend-api';
import { HealthcareProviderLocation } from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { WaitlistEntry } from '@dreryk/egabinet-waitlist-frontend-api';

import {
   createSingleInterpolatedTranslationKey,
   practitionerNameFormatter,
   useFormController,
   useTranslate,
} from '@dreryk/react-common';
import { FormController, StyledFlex, TextView } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { PractitionerSafePresenter } from '~libs.desktop.shared.practitioner-safe-presenter';
import { PatientSimpleCreateAction } from '~libs.desktop.shared.patient-simple-create-action';
import { PatientGroupTags } from '~libs.desktop.patient-groups';

import receptionService from '../../../../receptionService';
import { appointmentsTranslationKeys, waitlistEntryTranslationKeys } from '../../../../translations';
import {
   AppointmentTimeSlotOccupiedErrorHandlerProps,
   useAppointmentTimeSlotOccupiedErrorHandler,
} from '../form/AppointmentForm.hooks';
import { StyledAppointmentForm, StyledWarnings } from '../form/AppointmentForm.styled';
import {
   healthcareProvidingLocationViewVisitor,
   patientCreateAvailable,
   practitionerViewVisitor,
} from '../form/AppointmentForm.utils';
import { AppointmentDateTimeRangePicker } from '../form/components/AppointmentDateTimeRangePicker';
import { AppointmentNote } from '../form/components/AppointmentNote';
import { HealthcareProviderLocationSelect } from '../form/fields/healthcareProviderLocation/HealthcareProviderLocationSelect';
import { HealthcareProviderLocationTextView } from '../form/fields/healthcareProviderLocation/HealthcareProviderLocationTextView';
import { PatientAutosuggest } from '../form/fields/patient/PatientAutosuggest';
import { PractitionerSelect } from '../form/fields/PractitionerSelect/PractitionerSelect';
import { EstimatedDurationField } from '../form/fields/EstimatedDurationField';
import { VideoConsultationCheckbox } from '../form/fields/VideoConsultationCheckbox';
import { VideoConsultationWarning } from '../form/components/VideoConsultationWarning';
import { ServiceLocationWarning } from '../form/components/ServiceLocationWarning';
import { DateConfirmedWithPatientField } from '../../create/waitlist/fields/DateConfirmedWithPatientField';
import { IncompatiblePreferenceNotification } from '../../create/waitlist/IncompatiblePreferenceNotification';
import {
   createIncompatiblePreferenceNotificationChecker,
   createPriorityPreferenceChecker,
} from '../../create/waitlist/AppointmentWaitlistCreateForm.utils';
import {
   AppointmentWaitlistCreateFormBean,
   AppointmentWaitlistCreateFormController,
   AppointmentWaitlistCreateFormInitializer,
} from '../../create/waitlist/AppointmentWaitlistCreateForm.types';

export type AppointmentWaitlistFormRenderProps = Pick<
   AppointmentWaitlistCreateFormController,
   'handleSubmit' | 'formState' | 'trigger' | 'getValue'
> &
   AppointmentTimeSlotOccupiedErrorHandlerProps;

type AppointmentWaitlistFormRenderer = (renderProps: AppointmentWaitlistFormRenderProps) => ReactElement;

type AppointmentWaitlistFormProps = {
   readonly children: AppointmentWaitlistFormRenderer;
   readonly initializer: AppointmentWaitlistCreateFormInitializer;
   readonly appointmentId?: number;
   readonly practitioners: AppointmentPractitioner[];
   readonly healthcareProviderLocations: HealthcareProviderLocation[];
   readonly waitlistEntry: WaitlistEntry;
   readonly patientCreateAllowed?: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const AppointmentWaitlistForm = (props: AppointmentWaitlistFormProps): JSX.Element => {
   const translate = useTranslate();
   const { register, handleSubmit, watch, setValue, trigger, control, formState, getValue, setError, sync } =
      // eslint-disable-next-line react-hooks/exhaustive-deps
      useFormController<AppointmentWaitlistCreateFormBean>(useMemo(() => props.initializer(), [props.initializer]));
   const bean = watch();
   const shouldShowIncompatiblePreferenceNotification = createIncompatiblePreferenceNotificationChecker(
      bean,
      props.waitlistEntry
   );
   const isPriorityPreference = createPriorityPreferenceChecker(props.waitlistEntry);

   return (
      <>
         <StyledAppointmentForm noValidate autoComplete={'off'}>
            <StyledFlex direction={'column'} gap={'8px'}>
               {healthcareProvidingLocationViewVisitor(
                  props.healthcareProviderLocations,
                  bean.healthcareProviderLocation,
                  formState.touched.healthcareProviderLocation
               )({
                  text: ({ editable }) => (
                     <HealthcareProviderLocationTextView register={register} editable={editable} />
                  ),
                  select: () => (
                     <HealthcareProviderLocationSelect
                        register={register}
                        healthcareProviderLocations={props.healthcareProviderLocations}
                     />
                  ),
               })}
               {shouldShowIncompatiblePreferenceNotification('location') ? (
                  <IncompatiblePreferenceNotification
                     testId={'incompatibleLocation'}
                     label={createSingleInterpolatedTranslationKey(
                        isPriorityPreference('location')
                           ? waitlistEntryTranslationKeys.priorityPreference
                           : waitlistEntryTranslationKeys.preference,
                        'label',
                        props.waitlistEntry.location!.name
                     )}
                     warning={isPriorityPreference('location')}
                  />
               ) : null}
            </StyledFlex>
            {protectFeature(
               'bookingVideoConsultations',
               <StyledFlex direction={'column'} gap={'8px'}>
                  <VideoConsultationCheckbox register={register} />{' '}
                  {shouldShowIncompatiblePreferenceNotification('encounterMode') ? (
                     <IncompatiblePreferenceNotification
                        testId={'incompatibleEncounterMode'}
                        label={createSingleInterpolatedTranslationKey(
                           waitlistEntryTranslationKeys.preference,
                           'label',
                           translate(waitlistEntryTranslationKeys.encounterModeMap[props.waitlistEntry.encounterMode])
                        )}
                     />
                  ) : null}
               </StyledFlex>,
               null
            )}
            <StyledFlex direction={'column'} gap={'8px'}>
               {practitionerViewVisitor(props.practitioners)({
                  single: () => (
                     <FormController<AppointmentPractitioner> controller={register('practitioner')}>
                        {({ value: practitioner }) => (
                           <TextView label={appointmentsTranslationKeys.specialist} testId={'specialist'}>
                              <PractitionerSafePresenter
                                 testId={'practitioner'}
                                 practitioner={practitioner}
                                 type={'text'}
                                 withSpecialties
                              />
                           </TextView>
                        )}
                     </FormController>
                  ),
                  multiple: () => <PractitionerSelect register={register} practitioners={props.practitioners} />,
               })}
               {shouldShowIncompatiblePreferenceNotification('practitioner') ? (
                  <IncompatiblePreferenceNotification
                     testId={'incompatiblePractitioner'}
                     label={createSingleInterpolatedTranslationKey(
                        isPriorityPreference('practitioner')
                           ? waitlistEntryTranslationKeys.priorityPreference
                           : waitlistEntryTranslationKeys.preference,
                        'label',
                        practitionerNameFormatter(props.waitlistEntry.practitioner, {
                           options: { order: 'firstLast' },
                           translator: translate,
                        })!
                     )}
                     warning={isPriorityPreference('practitioner')}
                  />
               ) : null}
            </StyledFlex>
            <StyledFlex gap={8} direction={'column'}>
               <StyledFlex gap={'24px'}>
                  <PatientAutosuggest register={register} name={'patient'} disabled />
                  {patientCreateAvailable(bean.patient, props.patientCreateAllowed) ? (
                     <PatientSimpleCreateAction
                        onSuccess={(patient) => {
                           setValue('patient', patient);
                           trigger('patient');
                        }}
                     />
                  ) : null}
               </StyledFlex>
               <PatientGroupTags patient={bean.patient} />
            </StyledFlex>
            <StyledFlex gap={'24px'}>
               <AppointmentDateTimeRangePicker
                  control={control}
                  appointmentId={props.appointmentId}
                  formState={formState}
                  setValue={setValue}
                  register={register}
                  getValue={getValue}
                  trigger={trigger}
                  setError={setError}
                  watch={watch}
               />

               <EstimatedDurationField register={register} watch={watch} setValue={setValue} />
            </StyledFlex>
            <AppointmentNote register={register} note={bean.note} />
            <StyledFlex direction={'column'} gap={'8px'}>
               {bean.practitioner ? (
                  <receptionService.calendar.appointment.ReceptionServiceInteractiveTable
                     practitionerId={bean.practitioner.id}
                     register={register}
                     control={control}
                     sync={sync}
                     setValue={setValue}
                  />
               ) : null}
               {protectFeature(
                  'searchingPrivateMedicalServices',
                  shouldShowIncompatiblePreferenceNotification('service') ? (
                     <IncompatiblePreferenceNotification
                        testId={'incompatibleServices'}
                        label={createSingleInterpolatedTranslationKey(
                           waitlistEntryTranslationKeys.preference,
                           'label',
                           props.waitlistEntry.receptionService!.name
                        )}
                        warning
                     />
                  ) : null
               )}
            </StyledFlex>
            <StyledWarnings>
               {protectFeature(
                  'bookingVideoConsultations',
                  <VideoConsultationWarning watch={watch} control={control} />
               )}
               <ServiceLocationWarning watch={watch} control={control} />
            </StyledWarnings>
            <DateConfirmedWithPatientField register={register} />
         </StyledAppointmentForm>

         {props.children({
            handleSubmit: handleSubmit,
            formState: formState,
            setAppointmentTimeSlotOccupiedError: useAppointmentTimeSlotOccupiedErrorHandler(setError),
            getValue: getValue,
            trigger: trigger,
         })}
      </>
   );
};
